import { Formik, Form } from 'formik';

import { FormActions } from '@@/form-components/FormActions';

import { KubernetesSetupFormData } from './types';
import { KubernetesSetupInnerForm } from './KubernetesSetupInnerForm';

interface Props {
  initialValues: KubernetesSetupFormData;
  onSubmit: (values: KubernetesSetupFormData) => void | Promise<void>;
  /** Mode: 'create' | 'edit' */
  mode: 'create' | 'edit';
  /** Timezone for change window configuration */
  timeZone?: string;
  /** Callback when timezone changes */
  onChangeTimeZone?: (timeZone: string) => void;
  /** Whether the policy name has been provided (for validation) */
  nameProvided: boolean;
  /** Whether the environment groups have been provided (for validation) */
  environmentGroupsProvided: boolean;
}

/**
 * Kubernetes Setup Policy Form Component
 *
 * Renders the setup configuration form for Kubernetes policies.
 * Can be used for both creating new policies and editing existing ones.
 */
export function KubernetesSetupPolicyForm({
  initialValues,
  onSubmit,
  mode,
  timeZone = 'UTC',
  onChangeTimeZone = () => {},
  nameProvided,
  environmentGroupsProvided,
}: Props) {
  return (
    <Formik<KubernetesSetupFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ values, setFieldValue, isValid, isSubmitting }) => (
        <Form>
          <KubernetesSetupInnerForm
            formData={values}
            onChange={(key, value) => setFieldValue(key, value)}
            timeZone={timeZone}
            onChangeTimeZone={onChangeTimeZone}
          />

          <FormActions
            submitLabel={mode === 'create' ? 'Create policy' : 'Update policy'}
            loadingText={mode === 'create' ? 'Creating...' : 'Updating...'}
            isLoading={isSubmitting}
            isValid={isValid && !!nameProvided && !!environmentGroupsProvided}
            data-cy="policy-form-actions"
          />
        </Form>
      )}
    </Formik>
  );
}
